/**
 * Menus (PRD §21/§27): title, pause, refit, chart and the two end cards.
 *
 * Everything here is drawn into the same 2-D overlay the HUD uses, in the same
 * instrument language — cyan/amber monospace, segmented gauges, corner-ticked
 * frames. A menu is a panel on the same machine, not a web page bolted on top.
 *
 * The shared drawing primitives live here rather than in `hud.ts` so that the
 * dependency runs one way: the HUD imports the menus, never the reverse.
 *
 * Navigation is deliberately input-agnostic. Every screen is a list of `Row`s;
 * a row is picked with the stick and the fire key, or by being tapped. The rows
 * are rebuilt from world state every frame and remember the rectangle they were
 * last drawn at, which is the entire hit-test.
 */

import type { World } from '../game/world';
import type { Input } from '../core/input';
import type { TouchInput } from '../core/touch';
import { UPGRADES, UPGRADE_BAYS, WORLD, type UpgradeId } from '../game/config';
import { HOLD_CAPACITY } from '../game/ore';
import { MINERAL_TYPES } from '../render/mineralMesh';
import { CHASSIS_LIST, type ChassisId } from '../game/chassis';
import { MODE_LIST } from '../game/modes';
import type { Stats } from '../game/upgrades';
import { loadSurvivalRecord, survivalOffer, survivalRun } from '../game/survival';
import type { ModeId } from '../game/mode';
import { costOf, maxLevel, nextBlurb } from '../game/upgrades';
import { savedModes } from '../game/save';
import { profileY, STATIONS } from '../game/level';
import { clamp } from '../core/math';

// ---------------------------------------------------------------------------
// Palette + primitives, shared with the HUD
// ---------------------------------------------------------------------------

export const CYAN = '#5fe4e0';
export const DIM = '#1d5b62';
export const AMBER = '#ffb454';
export const RED = '#ff5a4a';
/** Shields read cold: the one blue on the panel that is not the house cyan. */
export const ICE = '#6ab8ff';
export const MAGENTA = '#ff6ad5';
export const VIOLET = '#c98cff';
export const PALE = '#bfe9ff';
export const WHITE = '#e8fdff';

export const MONO = 'ui-monospace, "SF Mono", Menlo, monospace';
const TAU = Math.PI * 2;

export function drawLabel(
  c: CanvasRenderingContext2D, text: string, x: number, y: number,
  color = DIM, size = 10, align: CanvasTextAlign = 'left', tracking = 2,
) {
  c.font = `500 ${size}px ${MONO}`;
  c.fillStyle = color;
  c.textAlign = align;
  c.letterSpacing = `${tracking}px`;
  c.fillText(text, x, y);
  c.letterSpacing = '0px';
}

export function drawValue(
  c: CanvasRenderingContext2D, text: string, x: number, y: number,
  color = CYAN, size = 22, align: CanvasTextAlign = 'left', tracking = 1,
) {
  c.font = `600 ${size}px ${MONO}`;
  c.fillStyle = color;
  c.textAlign = align;
  c.letterSpacing = `${tracking}px`;
  c.fillText(text, x, y);
  c.letterSpacing = '0px';
}

/** Segmented bar in the style of an arcade cabinet gauge. */
export function drawBar(
  c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number,
  t: number, color: string, segments = 20,
) {
  const gap = 2;
  const sw = (w - gap * (segments - 1)) / segments;
  const lit = t * segments;
  for (let i = 0; i < segments; i++) {
    const f = clamp(lit - i, 0, 1);
    c.fillStyle = f > 0 ? color : 'rgba(255,255,255,0.07)';
    c.globalAlpha = f > 0 ? 0.35 + f * 0.65 : 1;
    c.fillRect(x + i * (sw + gap), y, sw, h);
  }
  c.globalAlpha = 1;
}

/** Hairline box with corner ticks — the frame every readout sits in. */
export function drawFrame(
  c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number,
  edge = 'rgba(95,228,224,0.16)', tick = 'rgba(95,228,224,0.45)', arm = 7,
) {
  c.strokeStyle = edge;
  c.lineWidth = 1;
  c.strokeRect(x + 0.5, y + 0.5, w, h);
  c.strokeStyle = tick;
  for (let i = 0; i < 4; i++) {
    const dx = i === 0 || i === 2 ? 1 : -1;
    const dy = i < 2 ? 1 : -1;
    const cx = dx > 0 ? x : x + w;
    const cy = dy > 0 ? y : y + h;
    c.beginPath();
    c.moveTo(cx + 0.5 + dx * arm, cy + 0.5);
    c.lineTo(cx + 0.5, cy + 0.5);
    c.lineTo(cx + 0.5, cy + 0.5 + dy * arm);
    c.stroke();
  }
}

/** Salvage mark. Small enough to sit inline with a number. */
export function drawDiamond(c: CanvasRenderingContext2D, x: number, y: number, r: number, color: string) {
  c.fillStyle = color;
  c.beginPath();
  c.moveTo(x, y - r);
  c.lineTo(x + r * 0.72, y);
  c.lineTo(x, y + r);
  c.lineTo(x - r * 0.72, y);
  c.closePath();
  c.fill();
}

/** A spare hull, drawn as a sub rather than written as a number. */
export function drawSubGlyph(c: CanvasRenderingContext2D, x: number, y: number, r: number, color: string) {
  c.fillStyle = color;
  c.beginPath();
  c.ellipse(x, y, r, r * 0.42, 0, 0, TAU);
  c.fill();
  // Conning tower.
  c.fillRect(x - r * 0.16, y - r * 0.78, r * 0.36, r * 0.42);
  // Tail fin.
  c.beginPath();
  c.moveTo(x - r * 0.78, y);
  c.lineTo(x - r * 1.3, y - r * 0.62);
  c.lineTo(x - r * 1.12, y);
  c.lineTo(x - r * 1.3, y + r * 0.62);
  c.closePath();
  c.fill();
}

/** 0..1 as a level label. OFF reads better than 0%. */
const pct = (v: number) => (v <= 0.001 ? 'OFF' : `${Math.round(v * 100)}%`);

export function formatTime(seconds: number): string {
  const t = Math.max(0, Math.floor(seconds));
  const m = Math.floor(t / 60);
  const s = t % 60;
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}

// ---------------------------------------------------------------------------
// Screens
// ---------------------------------------------------------------------------

/**
 * What the readout says, in order.
 *
 * Absolute numbers where the player has an intuition for the unit — tonnes,
 * metres, hit points — and multipliers where they do not, because "1.4x thrust"
 * carries more than a force in newtons ever will. Kept to a dozen: this is the
 * shape of the boat at a glance, not its full stat block.
 */
interface Spec {
  name: string;
  get: (s: Stats) => string;
}

const SPECS: Spec[] = [
  { name: 'HULL', get: (s) => `${Math.round(s.maxHull)}` },
  { name: 'SHIELD', get: (s) => (s.maxShield > 0 ? `${Math.round(s.maxShield)}` : '—') },
  { name: 'ENERGY', get: (s) => `${Math.round(s.maxEnergy)}` },
  { name: 'THRUST', get: (s) => `${s.thrustMul.toFixed(2)}x` },
  { name: 'TOP SPEED', get: (s) => `${s.topSpeedMul.toFixed(2)}x` },
  { name: 'LIFT', get: (s) => `${s.liftCap.toFixed(1)} t` },
  { name: 'TOW DRAG', get: (s) => `${s.haulMul.toFixed(2)}x` },
  { name: 'IMPACT', get: (s) => `${s.impactMul.toFixed(2)}x` },
  { name: 'BLAST', get: (s) => `${s.blastMul.toFixed(2)}x` },
  { name: 'CANNON', get: (s) => `${s.cannon.shots}x  ${s.cannonDamage.toFixed(0)}` },
  { name: 'TORPEDOES', get: (s) => `${s.torpedoMax}` },
  { name: 'SONAR', get: (s) => `${Math.round(s.sonarRange)} m` },
  { name: 'LAMP', get: (s) => `${Math.round(s.lampRange)} m` },
  { name: 'BURNER', get: (s) => `${Math.round(s.nitroMax)}` },
];

/** OFF, then four steps. HIGH is what everything was tuned at. */
export const GFX_LEVELS = ['OFF', 'LOW', 'MEDIUM', 'HIGH', 'ULTRA'] as const;

export type Screen = 'none' | 'title' | 'paused' | 'shop' | 'map' | 'gameover' | 'victory' | 'options' | 'modes' | 'cards' | 'hold' | 'bay';
export type MenuCommand =
  | 'resume' | 'newgame' | 'continue' | 'restart' | 'title' | 'toggleMute'
  | 'cycleCap' | 'cycleTier' | 'cycleHull' | 'cycleDof' | 'cycleAA' | 'cycleSsfx' | 'cycleTod' | 'cycleIbl' | 'cycleBright' | 'cycleAo' | 'cycleShadow' | 'cycleSsr' | 'cycleSky' | 'cycleAf' | 'cycleRes' | 'openTuner'
  | 'cycleMusic' | 'cycleSfx' | 'cycleAmb'
  /** A mode was chosen; `pickedMode` says which. */
  | 'pickMode'
  /** A run-scoped upgrade was chosen; `pickedCard` says which. */
  | 'pickCard';

/** One selectable line. The rectangle is filled in by whatever drew it. */
interface Row {
  id: string;
  label: string;
  sub: string;
  /** Navigable and pickable. */
  enabled: boolean;
  upgrade: UpgradeId | null;
  /** Shop only: this row hands cargo over instead of buying a module. */
  sells: boolean;
  /** Shop only: this row buys or boards a hull. */
  hull: ChassisId | null;
  /** Price, for a shop row. null = maxed. */
  cost: number | null;
  /** Shop only: within the current salvage balance. */
  afford: boolean;
  /**
   * A QUANTITY THIS ROW CARRIES, adjusted with left/right before confirming.
   *
   * The menu has no slider and does not want one — every other adjustable
   * thing in it cycles on activate, which works for four brightness steps and
   * not at all for "sell some number between 1 and 26 of these". So the
   * selected row takes left and right instead of the list taking them, which
   * is the smallest change that makes a quantity choosable at all: one row is
   * ever in that mode, and up and down still navigate.
   */
  qty: { value: number; max: number; each: number; ore: number } | null;
  x: number; y: number; w: number; h: number;
}

/** Everything the menus need to know about the frame they are drawn into. */
export interface MenuView {
  w: number;
  h: number;
  /** Unit scale, from the shorter screen axis. */
  s: number;
  compact: boolean;
  safe: { top: number; bottom: number; left: number; right: number };
}

const lcg = (seed: number) => {
  let s = seed >>> 0;
  return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
};

export class Menus {
  screen: Screen = 'none';
  /** Set by the HUD each frame so the pause menu can report the audio state. */
  muted = false;
  /** Frame ceiling in fps, 0 = uncapped. Mirrored from the shell's settings. */
  frameCap = 60;
  /** Quality tier preference, mirrored the same way. */
  tierPref = 'auto';
  /** Which submarine hull is selected, mirrored the same way. */
  hullPref: 'mk2' | 'model' | 'classic' = 'mk2';
  /** Whether depth of field is on, mirrored the same way as the rest. */
  /** Aperture, as an f-number; 0 is off. */
  dofStop = 0;
  ssfx = true;
  ibl = true;
  brightness = 1;
  gfxAo = 3;
  gfxShadow = 3;
  gfxSsr = 3;
  gfxSky = 3;
  /** Anisotropic filtering, as the sample count itself. See `Settings.gfxAf`. */
  gfxAf = 8;
  /** Render scale multiplier on the tier budget; 0 is native. */
  gfxRes = 1;
  todName = '';

  /**
   * Tag a blurb for a setting that cannot swap live.
   *
   * Mid-dive it says so and the reload is deferred; from the title it just
   * happens, so there is nothing to warn about.
   */
  private applyNote(blurb: string): string {
    return this.returnTo === 'paused' ? `${blurb}  ·  NEXT LAUNCH` : blurb;
  }
  /** Antialiasing preference, and what 'auto' actually resolved to. */
  aaPref = 'auto';
  aaActual = '';
  /**
   * Something on this screen needs a fresh renderer. Only says so on the way
   * out, where the restart actually happens — a row that shouted it while you
   * were still cycling would be shouting at every press.
   */
  pendingReload = false;
  /** Set alongside a `pickMode` command. */
  pickedMode: ModeId = 'campaign';
  /** Set alongside a `pickCard` command. */
  pickedCard = '';

  /** Bus levels, mirrored from the shell's settings. */
  musicVol = 0.8;
  sfxVol = 1;
  ambVol = 1;
  /** The tier actually running, which is what 'AUTO' resolved to. */
  tierActual = '';

  private rows: Row[] = [];
  private sel = 0;
  private time = 0;
  /** Suppresses the keypress that opened the screen from also picking a row. */
  private guard = 0;
  /** >0 just after a successful purchase, <0 after a refused one. */
  private flash = 0;
  /** Where `back` goes from a screen that was opened over another one. */
  /**
   * Where the turntable goes, as FRACTIONS of the viewport, or null when the
   * shop is shut.
   *
   * Fractions rather than pixels because this canvas measures in CSS units and
   * the renderer in device ones, and on any scaled display the two disagree. A
   * fraction is the same number in both.
   *
   * Read by `main` and handed to the renderer. The HUD cannot draw a mesh — it
   * is a 2-D context over the 3-D canvas — so it leaves a hole and publishes
   * the coordinates, and the renderer scissors its turntable pass into it.
   */
  modelRect: { u: number; v: number; wf: number; hf: number } | null = null;

  /**
   * Which bay the 'bay' screen is showing. One field rather than a screen per
   * bay: the bays differ only in which rows they list, and four near-identical
   * `Screen` members would be four of everything that switches on one.
   */
  private bay = UPGRADE_BAYS[0]!.id;

  /**
   * Where the cursor was in the shop when a sub-screen was opened.
   *
   * `open` resets `sel` to 0, which is right arriving anywhere and wrong coming
   * BACK: a second tier means the player leaves the refit list part way down it
   * several times a visit, and landing on the cargo row each time is a menu
   * that loses your place every time you use it. The shop's rows are rebuilt
   * identically, so the index is the position.
   */
  private shopSel = 0;

  private returnTo: Screen = 'none';

  /**
   * True when the options screen was reached from the pause screen, which only
   * exists during a dive. `main` uses it to decide whether a setting that needs
   * a reload may take one now or must wait.
   */
  get optionsInDive(): boolean {
    return this.screen === 'options' && this.returnTo === 'paused';
  }
  /**
   * Whether there is a run to continue. Sampled when a screen opens rather than
   * read per frame — see the note in `build`. The title is the only screen that
   * asks, and the answer cannot change while it is up.
   */
  /** Modes with a run waiting, refreshed whenever the title opens. */
  private savedRuns: string[] = savedModes();
  /** Which mode the last CONTINUE row picked, for `main` to enter. */
  resumeMode = '';

  /**
   * True while a screen is layered over the title rather than over a dive.
   * The shell uses it to keep the attract loop running and the flight
   * instruments hidden: options opened from the title is still the title, and
   * a depth gauge reading 39 m behind it would be reporting on a boat nobody
   * is flying.
   */
  get overTitle(): boolean {
    return this.returnTo === 'title';
  }

  /** Drifting marine snow on the title card. Built once, animated by time. */
  private motes: { x: number; y: number; r: number; sp: number; ph: number }[] = [];
  /** Contacts the title screen's sonar sweep paints. */
  private blips: { a: number; d: number }[] = [];
  private mapY = new Float32Array(260);

  constructor(private ctx: CanvasRenderingContext2D) {
    const rnd = lcg(0xab7551);
    for (let i = 0; i < 120; i++) {
      this.motes.push({ x: rnd(), y: rnd(), r: 0.4 + rnd() * 1.9, sp: 0.008 + rnd() * 0.03, ph: rnd() * TAU });
    }
    for (let i = 0; i < 9; i++) {
      this.blips.push({ a: rnd() * TAU, d: 0.28 + rnd() * 0.68 });
    }
  }

  // --- state ---------------------------------------------------------------

  open(s: Screen, touch: TouchInput | null) {
    const from = this.returnTo;
    if (s === 'title') this.savedRuns = savedModes();
    this.screen = s;
    this.sel = 0;
    this.rows.length = 0;
    // `options` sets its own return target immediately after opening; every
    // other screen starts fresh.
    this.returnTo = s === 'options' ? from : 'none';
    this.flash = 0;
    this.guard = 0.18;
    if (!touch) return;
    touch.menuMode = s !== 'none';
    if (s !== 'none') touch.releaseAll();
    // Whatever tap opened (or closed) the screen must not also press a row.
    touch.consumeTaps();
  }

  // --- update --------------------------------------------------------------

  update(input: Input, world: World, dt: number, touch: TouchInput | null): MenuCommand | null {
    this.time += dt;
    this.flash -= this.flash * Math.min(1, dt * 4);
    if (Math.abs(this.flash) < 0.02) this.flash = 0;
    if (this.screen === 'none') {
      if (touch) touch.menuMode = false;
      return null;
    }
    if (touch) touch.menuMode = true;

    const rows = this.build(world);
    const st = input.state;

    if (this.guard > 0) {
      this.guard -= dt;
      touch?.consumeTaps();
      return null;
    }

    // A row with a quantity takes left and right; the list keeps up and down.
    // Only one row is ever in this mode, so nothing else has to know.
    const stepper = rows[this.sel]?.qty ?? null;
    if (stepper) {
      // Coarse on a big stack: stepping one at a time through twenty-six is a
      // chore, and the useful answers are "all of it", "half", "one".
      const step = stepper.max > 12 ? Math.max(1, Math.round(stepper.max / 8)) : 1;
      if (st.menuLeft) stepper.value = Math.max(1, stepper.value - step);
      if (st.menuRight) stepper.value = Math.min(stepper.max, stepper.value + step);
    } else {
      if (st.menuLeft) this.move(-1);
      if (st.menuRight) this.move(1);
    }
    if (st.menuUp) this.move(-1);
    if (st.menuDown) this.move(1);

    let chosen: Row | null = st.confirm ? rows[this.sel] ?? null : null;

    // Taps are hit-tested against the rectangles the last draw recorded.
    const taps = touch?.consumeTaps();
    if (taps) {
      for (const t of taps) {
        for (let i = 0; i < rows.length; i++) {
          const r = rows[i];
          if (r.h <= 0) continue;
          if (t.x < r.x || t.x > r.x + r.w || t.y < r.y || t.y > r.y + r.h) continue;
          this.sel = i;
          chosen = r;
          break;
        }
      }
    }

    if (st.back) return this.back(touch);
    if (chosen && chosen.enabled) return this.activate(chosen, world, touch);
    return null;
  }

  private move(dir: number) {
    const n = this.rows.length;
    if (!n) return;
    for (let i = 1; i <= n; i++) {
      const k = (((this.sel + dir * i) % n) + n) % n;
      if (this.rows[k].enabled) { this.sel = k; return; }
    }
  }

  private activate(row: Row, world: World, touch: TouchInput | null): MenuCommand | null {
    switch (this.screen) {
      case 'title':
        if (row.id.startsWith('continue-')) {
          this.resumeMode = row.id.slice(9);
          this.open('none', touch);
          return 'continue';
        }
        // NEW DIVE picks a mode first. `returnTo` makes BACK land on the title
        // rather than dropping the player into a world nobody chose.
        // With one mode there is nothing to choose, and a card with a single
        // row is a worse experience than no card. The screen earns its place
        // when a second mode exists.
        if (row.id === 'newgame') {
          if (MODE_LIST.length < 2) { this.open('none', touch); return 'newgame'; }
          this.open('modes', touch);
          this.returnTo = 'title';
          return null;
        }
        if (row.id === 'options') { this.open('options', touch); this.returnTo = 'title'; return null; }
        return null;

      case 'paused':
        if (row.id === 'resume') { this.open('none', touch); return 'resume'; }
        if (row.id === 'refit') { this.open('shop', touch); this.returnTo = 'paused'; return null; }
        if (row.id === 'options') { this.open('options', touch); this.returnTo = 'paused'; return null; }
        if (row.id === 'mute') return 'toggleMute';
        if (row.id === 'abandon') { this.open('title', touch); return 'title'; }
        return null;

      case 'options': {
        // The list is rebuilt from the mirrored values every frame, so a row
        // relabels itself the moment the shell applies the change.
        const at = this.sel;
        if (row.id === 'leave') return this.back(touch);
        if (row.id === 'mute') { this.sel = at; return 'toggleMute'; }
        if (row.id === 'music') { this.sel = at; return 'cycleMusic'; }
        if (row.id === 'sfx') { this.sel = at; return 'cycleSfx'; }
        if (row.id === 'amb') { this.sel = at; return 'cycleAmb'; }
        if (row.id === 'cap') { this.sel = at; return 'cycleCap'; }
        if (row.id === 'tier') { this.sel = at; return 'cycleTier'; }
        if (row.id === 'hull') { this.sel = at; return 'cycleHull'; }
        if (row.id === 'dof') { this.sel = at; return 'cycleDof'; }
        if (row.id === 'ssfx') { this.sel = at; return 'cycleSsfx'; }
        if (row.id === 'tuner') { this.sel = at; return 'openTuner'; }
        if (row.id === 'ibl') { this.sel = at; return 'cycleIbl'; }
        if (row.id === 'bright') { this.sel = at; return 'cycleBright'; }
        if (row.id === 'ao') { this.sel = at; return 'cycleAo'; }
        if (row.id === 'shadow') { this.sel = at; return 'cycleShadow'; }
        if (row.id === 'ssr') { this.sel = at; return 'cycleSsr'; }
        if (row.id === 'af') { this.sel = at; return 'cycleAf'; }
        if (row.id === 'res') { this.sel = at; return 'cycleRes'; }
        if (row.id === 'sky') { this.sel = at; return 'cycleSky'; }
        if (row.id === 'tod') { this.sel = at; return 'cycleTod'; }
        if (row.id === 'aa') { this.sel = at; return 'cycleAA'; }
        return null;
      }

      case 'hold': {
        if (row.id === 'leave') { this.toShop(touch); return null; }
        if (row.id === 'sellall') { this.flash = world.sellHold() > 0 ? 1 : -1; return null; }
        if (row.qty) {
          this.flash = world.sellOre(row.qty.ore, row.qty.value) > 0 ? 1 : -1;
          // The stack shrank, so the row's ceiling has to come down with it or
          // the next confirm would ask to sell shards that are already gone.
          row.qty.max = Math.max(0, row.qty.max - row.qty.value);
          row.qty.value = Math.max(1, Math.min(row.qty.value, row.qty.max));
          return null;
        }
        return null;
      }

      case 'bay': {
        if (row.id === 'leave') { this.toShop(touch); return null; }
        this.flash = row.upgrade && row.cost !== null && world.buy(row.upgrade, row.cost) ? 1 : -1;
        return null;
      }

      case 'shop':
        if (row.id === 'leave') return this.back(touch);
        if (row.id === 'hold') { this.shopSel = this.sel; this.open('hold', touch); return null; }
        if (UPGRADE_BAYS.some((b) => b.id === row.id)) {
          this.bay = row.id;
          this.shopSel = this.sel;
          this.open('bay', touch);
          return null;
        }
        if (row.sells) { this.flash = world.sell() > 0 ? 1 : -1; return null; }
        if (row.hull) { this.flash = world.buyHull(row.hull) ? 1 : -1; return null; }
        // A refusal is answered as loudly as a purchase; silence reads as a bug.
        this.flash = row.upgrade && row.cost !== null && world.buy(row.upgrade, row.cost) ? 1 : -1;
        return null;

      case 'modes': {
        if (row.id === 'leave') return this.back(touch);
        if (row.id.startsWith('mode-')) {
          this.pickedMode = row.id.slice(5) as ModeId;
          this.open('none', touch);
          return 'pickMode';
        }
        return null;
      }

      case 'cards': {
        if (row.id.startsWith('card-')) {
          this.pickedCard = row.id.slice(5);
          this.open('none', touch);
          return 'pickCard';
        }
        return null;
      }

      case 'map':
        return this.back(touch);

      case 'gameover':
        if (row.id === 'retry') { this.open('none', touch); return 'restart'; }
        if (row.id === 'abandon') { this.open('title', touch); return 'title'; }
        return null;

      case 'victory':
        if (row.id === 'resume') { this.open('none', touch); return 'resume'; }
        if (row.id === 'abandon') { this.open('title', touch); return 'title'; }
        return null;

      default:
        return null;
    }
  }

  /**
   * The modules in one bay, as the fields a row needs.
   *
   * Shared between the bay screen, which draws these, and the shop, which
   * counts them to decide what a bay's row should say. Two readings of the same
   * question — is this affordable — is how a summary starts lying.
   */
  /** Back to the refit list, on the row that opened the sub-screen. */
  private toShop(touch: TouchInput | null) {
    this.open('shop', touch);
    // After `open`, which zeroes it. Clamped on the next build, which is where
    // the rows come from — a hull bought inside a bay cannot change the count,
    // but a cargo row can disappear while the hold screen is open.
    this.sel = this.shopSel;
  }

  private bayRows(world: World, bayId: string) {
    const bay = UPGRADE_BAYS.find((b) => b.id === bayId) ?? UPGRADE_BAYS[0]!;
    const fitted = world.player.chassis.modules;
    return bay.members.map((id) => {
      const def = UPGRADES.find((d) => d.id === id)!;
      const canFit = fitted.includes(id);
      const lvl = world.levels[id];
      const cost = canFit ? costOf(id, lvl) : null;
      return {
        id: def.id as string,
        label: def.name,
        sub: canFit ? nextBlurb(id, lvl) : `NO MOUNT ON THE ${world.player.chassis.name}`,
        enabled: canFit,
        upgrade: id as UpgradeId | null,
        cost,
        afford: canFit && cost !== null && world.salvage >= cost,
      };
    });
  }

  private back(touch: TouchInput | null): MenuCommand | null {
    switch (this.screen) {
      case 'paused':
        this.open('none', touch);
        return 'resume';
      case 'hold':
      case 'bay':
        // Back from a sub-screen is the shop, never the water: the player came
        // here from a list they were part way through.
        this.toShop(touch);
        return null;

      case 'shop':
      case 'map':
      case 'options': {
        const to = this.returnTo;
        this.open(to, touch);
        return to === 'none' ? 'resume' : null;
      }
      default:
        // The title and the two end cards are terminal: there is nothing behind
        // them, and a stray Escape must not drop the player into a dead world.
        return null;
    }
  }

  // --- row model -----------------------------------------------------------

  private build(world: World): Row[] {
    const prev = this.rows;
    const out: Row[] = [];
    const push = (
      id: string, label: string, sub = '', enabled = true,
      upgrade: UpgradeId | null = null, cost: number | null = null, afford = true,
      sells = false, hull: ChassisId | null = null,
      qty: Row['qty'] = null,
    ) => {
      const old = prev.find((r) => r.id === id);
      // A quantity survives a rebuild — the list is rebuilt every frame, and a
      // stepper that reset to full on each one could not be stepped at all.
      if (qty && old?.qty && old.qty.ore === qty.ore) {
        qty.value = Math.max(1, Math.min(qty.max, old.qty.value));
      }
      out.push({
        id, label, sub, enabled, upgrade, cost, afford, sells, hull, qty,
        x: old ? old.x : 0, y: old ? old.y : 0, w: old ? old.w : 0, h: old ? old.h : 0,
      });
    };

    switch (this.screen) {
      case 'title':
        // Read once, not twice. `hasSave` JSON.parses the whole save — levels,
        // five id lists and up to 1200 crater floats — and `build()` runs every
        // frame behind the title's attract loop. It is ~20 KB today and nobody
        // would notice; it stops being free the moment the profile carries a
        // fleet and a discovery log.
        // ONE ROW PER WAITING RUN, named for its mode.
        //
        // A single CONTINUE resumed into whatever mode was loaded, so a descent
        // left behind to try an expedition could not be got back to — and
        // nothing on the screen admitted a run existed at all. Naming them is
        // most of the fix: two rows say there are two runs, which the one row
        // never could.
        const saved = this.savedRuns;
        for (const id of saved) {
          const rules = MODE_LIST.find((r) => r.id === id);
          push(`continue-${id}`, `CONTINUE  ${rules ? rules.label : id.toUpperCase()}`,
            'RESUME WHERE YOU LEFT IT');
        }
        push('newgame', 'NEW DIVE',
          MODE_LIST.length > 1 ? 'CHOOSE A MODE'
            : saved.length ? 'START OVER FROM THE SHELF' : 'DESCEND');
        push('options', 'OPTIONS', 'FRAME RATE, DETAIL, SOUND');
        break;

      case 'paused': {
        push('resume', 'RESUME');
        if (world.dockable) push('refit', 'REFIT', world.dockable.name);
        push('options', 'OPTIONS', 'FRAME RATE, SOUND');
        // Say what this row actually does. It saves on the way out, so in a
        // persisting mode nothing is abandoned at all: the dive comes straight
        // back from CONTINUE at the last station. Labelling that "ABANDON DIVE"
        // left a player parked on this screen with 290,000 points and no
        // visible way to try another mode — the only door out was the one that
        // reads like it throws the run away.
        if (!world.rules.persists) {
          push('abandon', 'END THE RUN', 'THE RUN ENDS HERE · NOTHING IS BANKED');
        } else if (world.lastStation) {
          const st = world.rules.content.stations.find((q) => q.id === world.lastStation);
          push('abandon', 'LEAVE THE DIVE',
            `SAVED · CONTINUE RESUMES AT ${st ? st.name : 'THE LAST STATION'}`);
        } else {
          push('abandon', 'LEAVE THE DIVE', 'NO STATION REACHED YET · THIS DIVE IS LOST');
        }
        break;
      }

      case 'options': {
        push(
          'cap',
          `FRAME LIMIT  ${this.frameCap ? `${this.frameCap} FPS` : 'UNLIMITED'}`,
          this.frameCap
            ? 'CAPS THE GPU. COOLER, QUIETER, LONGER BATTERY'
            : 'DRAWS AS FAST AS THE DISPLAY WILL TAKE',
        );
        // ONE LIST, WHEREVER IT IS OPENED FROM.
        //
        // There used to be two: the pause screen showed a subset, so changing
        // the detail tier or the hull meant abandoning the dive, going to the
        // title, changing it and diving again. Nobody wants a settings screen
        // badly enough to do that, and the rows that were hidden are exactly
        // the ones worth judging against the water you are actually flying
        // through rather than a menu background.
        //
        // The three that cannot swap live are marked instead of hidden, and
        // `pendingReload` is only acted on when there is no dive to lose.
        {
          const shown = this.tierPref === 'auto'
            ? `AUTO (${this.tierActual.toUpperCase()})`
            : this.tierPref.toUpperCase();
          push('tier', `DETAIL  ${shown}`, this.applyNote('PIXELS, PARTICLES AND LIGHT MARCH STEPS'));
          // Same reason as the tier: the hull is built once, when the world is,
          // so swapping it is a reload and belongs where there is no dive to
          // lose. On trial — the classic hull is still here and still flies.
          // Sample count is baked into every pipeline, so this reloads too.
          const AA_BLURB: Record<string, string> = {
            auto: 'OFF ON A HIGH-DPI SCREEN, TAA ON A 1080p ONE',
            off: 'NO EDGE SMOOTHING. FASTEST',
            fxaa: 'EDGES FOUND AFTER THE FACT. NEARLY FREE',
            msaa: 'FOUR SAMPLES PER PIXEL. BEST EDGES, DEAREST',
            taa: 'SAMPLES GATHERED OVER TIME. ALSO QUIETS THE BEAM',
          };
          push('aa', `ANTIALIASING  ${this.aaPref === 'auto' ? `AUTO (${this.aaActual})` : this.aaPref.toUpperCase()}`,
            this.applyNote(AA_BLURB[this.aaPref] ?? ''));
          const HULL_NAME = { mk2: 'MK II', model: 'SCAN', classic: 'CLASSIC' };
          const HULL_BLURB = {
            mk2: 'THE CAD REBUILD · 12,689 TRIANGLES · 11 MOVING PARTS',
            model: 'THE SCANNED REFERENCE · 10,741 TRIANGLES',
            classic: 'THE PROCEDURAL BOAT',
          };
          push('hull', `HULL  ${HULL_NAME[this.hullPref]}`, this.applyNote(HULL_BLURB[this.hullPref]));
          push('tod', `TIME OF DAY  ${this.todName}`,
            'SUN ANGLE, COLOUR AND HOW MUCH GETS IN');
          // A section, so the three that cost real milliseconds sit together
          // and can be read against each other. `enabled: false` makes a row a
          // heading: the cursor steps over it and it draws at a third alpha.
          push('gfx-head', 'GRAPHICS', 'WHAT THE FRAME SPENDS ITS TIME ON', false);
          const lv = (n: number) => GFX_LEVELS[Math.max(0, Math.min(4, n))];
          push('ao', `OCCLUSION  ${lv(this.gfxAo)}`,
            'CONTACT SHADOW IN CREVICES  ·  CHEAPEST OF THE THREE');
          push('shadow', `SHADOWS  ${lv(this.gfxShadow)}`,
            'THE SUN AND THE LAMP, CAST BY WHAT IS ON SCREEN');
          push('ssr', `REFLECTIONS  ${lv(this.gfxSsr)}`,
            'DEAREST, AND THE ONE THAT SHOWS ITS SAMPLE COUNT');
          push('sky', `SKY  ${lv(this.gfxSky)}`,
            'CLOUDS AND SUN ABOVE THE WATERLINE  ·  FREE BELOW IT');
          // `applyNote`, like TIER and AA and HULL: this one is baked into a
          // sampler at build time, so it is a rebuild, and the note is how
          // every other rebuilding row says so.
          // Live, unlike the two rows below it — `configure` re-reads the pixel
          // budget on resize, so this needs no restart note.
          push('res', `RENDER SCALE  ${this.gfxRes === 0 ? 'NATIVE' : `${this.gfxRes}X`}`,
            this.gfxRes === 0
              ? 'FULL DISPLAY RESOLUTION  ·  SHARPEST REFLECTIONS, DEAREST FRAME'
              : 'MULTIPLIES THE DETAIL TIER\u2019S PIXEL BUDGET  ·  1X IS THE OLD CAP');
          push('af', `TEXTURE FILTER  ${this.gfxAf > 1 ? `${this.gfxAf}X` : 'TRILINEAR'}`,
            this.applyNote(this.gfxAf > 1
              ? 'SHARP GROUND RUNNING AWAY FROM THE CAMERA'
              : 'PLAIN TRILINEAR  ·  THE SEABED GOES SOFT A FEW METRES OUT'));
          push('tuner', 'SKY & WATER TUNER',
            'SLIDERS FOR THE SKY, SURFACE AND CAUSTICS  ·  ALSO THE K KEY');
          push('ibl', `ENVIRONMENT  ${this.ibl ? 'CAPTURED' : 'STUDIO'}`,
            this.ibl
              ? 'LIT BY A REAL UNDERWATER SKY'
              : 'LIT BY THREE LAMPS AND A GUESS');
          push('ssfx', `SCREEN EFFECTS  ${this.ssfx ? 'ON' : 'OFF'}`,
            this.ssfx
              ? 'OCCLUSION, SHADOWS, REFLECTIONS, GLASS'
              : 'FLAT SHADING - THE CHEAPEST FRAME');
        }
        push('dof', `DEPTH OF FIELD  ${this.dofStop ? `f/${this.dofStop}` : 'OFF'}`,
          this.dofStop
            ? (this.dofStop <= 2.2
              ? 'WIDE OPEN. THE DISTANCE DISSOLVES'
              : this.dofStop >= 8
                ? 'STOPPED DOWN. THE SOFTNESS IS BARELY THERE'
                : 'THE PLAY LAYER SHARP, THE CITY AND THE FRONDS SOFT')
            : 'EVERYTHING SHARP AT EVERY DISTANCE');
        push('bright', `BRIGHTNESS  ${Math.round(this.brightness * 100)}%`,
          this.brightness > 1.05 ? 'LIFTED — FOR A DIM SCREEN'
            : this.brightness < 0.95 ? 'DARKER — DEEPER SHADOWS'
              : 'AS THE DEPTH BANDS WERE TUNED');
        push('mute', this.muted ? 'SOUND OFF' : 'SOUND ON', 'TOGGLE ALL AUDIO');
        // Three buses, because they are three different jobs: score you might
        // want off entirely, effects tell you what just happened, and the water
        // hum is atmosphere some people find fatiguing.
        push('music', `MUSIC  ${pct(this.musicVol)}`, 'SOUNDTRACK AND THE DEEP BED');
        push('sfx', `EFFECTS  ${pct(this.sfxVol)}`, 'WEAPONS, IMPACTS, CREATURES');
        push('amb', `WATER HUM  ${pct(this.ambVol)}`, 'THE OCEAN ITSELF');
        push('leave', this.pendingReload ? 'BACK  ·  RESTARTS TO APPLY' : 'BACK');
        break;
      }

      case 'hold': {
        // ONE ROW PER SEAM, and the row carries how many of it to sell. Left
        // and right dial the quantity, confirm sells that many — so "what" and
        // "how much" are the same two keys rather than a screen of steppers.
        //
        // Defaulted to the whole stack, because selling all of it is what
        // happens nine times in ten and a default that needs adjusting before
        // it does the common thing is a default in the wrong place.
        for (const q of world.holdManifest()) {
          // `sells` true, which is the row style that pays OUT — the same one
          // the cargo row uses. Without it a null cost falls through to the
          // module path, where null means MAXED, and a stack of silica would
          // have been drawn as an upgrade you already own.
          push(`ore-${q.key}`, q.label, `${q.n} ABOARD  ·  ${q.each} EACH`,
               true, null, null, true, true, null,
               { value: q.n, max: q.n, each: q.each, ore: q.i });
        }
        const all = world.holdManifest().reduce((a, q) => a + q.n * q.each, 0);
        if (all > 0) push('sellall', 'LAND THE WHOLE HOLD', `+${all} SALVAGE`);
        push('leave', 'BACK', `${world.holdUsed} / ${HOLD_CAPACITY} ABOARD  ·  ${world.holdSpare} SPARE`);
        break;
      }

      case 'bay': {
        // Built by the same helper the shop's summary counts with, so the two
        // cannot disagree about what is affordable — a bay that advertises a
        // purchase it does not then offer is worse than no summary at all.
        for (const r of this.bayRows(world, this.bay)) {
          push(r.id, r.label, r.sub, r.enabled, r.upgrade, r.cost, r.afford);
        }
        push('leave', 'BACK', `${world.salvage} SALVAGE`);
        break;
      }

      case 'shop': {
        // Cargo first, and only when there is some. Arriving with a load and
        // being shown a list of things you cannot yet afford — with no way to
        // turn the thing you just dragged four minutes into money — is the one
        // ordering that makes the whole haul feel pointless.
        const load = world.tow.load;
        if (load) {
          push(
            'sell', `DELIVER ${load.label}`,
            `${load.mass.toFixed(1)} T  ·  +${world.cargoValue} SALVAGE`,
            true, null, null, true, true,
          );
        }
        // THE HOLD, for the same reason the cargo row is first: arriving with
        // ore and being shown a price list you cannot afford, with no way to
        // turn what you just spent two minutes extracting into money, is the
        // ordering that makes the whole run feel pointless.
        const used = world.holdUsed;
        if (used > 0) {
          const worth = world.holdManifest().reduce((a, q) => a + q.n * q.each, 0);
          push('hold', `HOLD  ${used} / ${HOLD_CAPACITY}`,
               `${world.holdSpare} SPARE  ·  WORTH ${worth} SALVAGE`);
        }
        // ONE ROW PER BAY. What a bay's row has to answer is the question the
        // player actually arrived with — is there anything in here for me — so
        // it carries the cheapest thing inside it that is both fitted and
        // affordable, and says so. A bay that says AFFORDABLE FROM 140 is worth
        // opening; one that says nothing is worth walking past, and being able
        // to walk past nine rows is the entire point of grouping them.
        for (const bay of UPGRADE_BAYS) {
          const mods = this.bayRows(world, bay.id);
          const buyable = mods.filter((r) => r.afford && r.cost !== null);
          const cheapest = buyable.reduce(
            (a, r) => (a === null || r.cost! < a ? r.cost! : a), null as number | null);
          const maxed = mods.filter((r) => r.enabled && r.cost === null).length;
          const sub = cheapest !== null
            ? `${buyable.length} AFFORDABLE  ·  FROM ${cheapest} SALVAGE`
            : maxed === mods.length ? 'ALL MAXED' : bay.blurb;
          push(bay.id, bay.name, sub);
        }
        // The bay, under the modules. A hull is a bigger decision than a
        // module and it should read that way — but it does not deserve its own
        // screen while there are two of them, and burying it behind one is how
        // a player never finds out a second boat exists.
        for (const c of CHASSIS_LIST) {
          const owned = world.hulls.has(c.id);
          const active = world.player.chassis.id === c.id;
          push(
            `hull-${c.id}`,
            active ? `${c.name}  ·  IN THE WATER` : c.name,
            active ? c.blurb : owned ? `${c.blurb}  ·  BOARD` : c.blurb,
            !active, null, owned ? null : c.price,
            owned || world.salvage >= c.price, false, c.id,
          );
        }
        push('leave', 'LEAVE REFIT');
        break;
      }

      case 'modes': {
        for (const m of MODE_LIST) push(`mode-${m.id}`, m.label, m.blurb);
        push('leave', 'BACK');
        break;
      }

      case 'cards': {
        // No BACK. A pick is the price of the intermission continuing, which
        // is what stops it being a menu the player learns to skip.
        for (const { card, rank } of survivalOffer()) {
          push(`card-${card.id}`, card.name,
            `${card.ranks[Math.min(rank, card.ranks.length) - 1]}${rank > 1 ? `   ·   RANK ${rank}` : ''}`);
        }
        break;
      }

      case 'map':
        push('leave', 'CLOSE CHART');
        break;

      case 'gameover':
        push('retry', 'RETRY', 'BACK TO THE LAST STATION');
        push('abandon', 'ABANDON', 'BACK TO TITLE');
        break;

      case 'victory':
        push('resume', 'KEEP EXPLORING');
        push('abandon', 'END THE DIVE', 'BACK TO TITLE');
        break;

      default:
        break;
    }

    this.rows = out;
    if (this.sel >= out.length) this.sel = Math.max(0, out.length - 1);
    if (out.length && !out[this.sel].enabled) {
      // Never leave the cursor parked on something that cannot be picked.
      const first = out.findIndex((r) => r.enabled);
      if (first >= 0) this.sel = first;
    }
    return out;
  }

  // --- drawing -------------------------------------------------------------

  draw(world: World, view: MenuView) {
    // Republished every frame the shop is up, and dropped the moment it is not
    // — a stale rect would leave the renderer scissoring a turntable into a
    // screen that has moved on.
    this.modelRect = null;
    if (this.screen === 'none') return;
    if (!this.rows.length) this.build(world);
    switch (this.screen) {
      case 'title': this.title(view); break;
      case 'paused': this.paused(world, view); break;
      case 'shop':
      // THE SAME PANEL, and the missing line here was the freeze.
      //
      // `main.ts` pauses the world on `screen !== 'none'` while this switch
      // decides what to draw — so a screen the switch does not know about
      // stops the game and renders no menu over it. A still picture and no
      // UI, which is indistinguishable from a hang. The selling screen was
      // not broken; it was never drawn.
      case 'hold':
      case 'bay': this.shop(world, view); break;
      case 'map': this.map(world, view); break;
      case 'options': this.options_(view); break;
      case 'modes': this.modes_(view); break;
      case 'cards': this.cards_(view); break;
      case 'gameover': this.ending(world, view, false); break;
      case 'victory': this.ending(world, view, true); break;
      default: break;
    }
  }

  /** Darken the running game behind a panel. */
  private scrim(view: MenuView, alpha: number) {
    const c = this.ctx;
    c.fillStyle = `rgba(2,9,13,${alpha})`;
    c.fillRect(0, 0, view.w, view.h);
  }

  /** Returns the x just past the heading, for a subtitle on the same line. */
  /**
   * The right-hand rail: the boat, turning, and what it currently does.
   *
   * `modelRect` is left behind in view coordinates for the renderer to draw
   * into — the HUD is a 2-D canvas over the 3-D one, so it cannot draw a mesh.
   * What it can do is not paint over a hole and say where the hole is.
   */
  private specRail(
    world: World, view: MenuView, x: number, y: number, w: number,
    u: (v: number) => number,
  ) {
    const c = this.ctx;
    // Keeps roughly 4:3 against the wider rail, which is the shape a boat
    // seven metres long and two across wants to be framed in.
    const vh = u(224);

    // The viewport. Cleared to transparent so the 3-D pass beneath shows
    // through, then framed, so the hole reads as an instrument and not as a
    // rendering failure.
    c.clearRect(x, y, w, vh);
    // 0.22, down from 0.55. The world used to show through here and needed
    // holding back; there is a black card behind the hull now, so this is only
    // a tint over it — and at 0.55 it was dimming the model as much as the
    // background, which is the one thing it must not do.
    c.fillStyle = 'rgba(2,14,20,0.22)';
    c.fillRect(x, y, w, vh);
    drawFrame(c, x, y, w, vh, 'rgba(95,228,224,0.18)', 'rgba(95,228,224,0.45)', u(12));
    this.modelRect = {
      u: (x + w / 2) / view.w, v: (y + vh / 2) / view.h,
      wf: w / view.w, hf: vh / view.h,
    };

    // WHAT IS SELECTED, NOT ALWAYS THE BOAT. On the hold screen the row under
    // the cursor is a mineral, and a turntable that answers every row with the
    // same hull is a turntable nobody looks at. The panel is the only place in
    // the game a shard can be seen at a size worth seeing.
    const ore = this.showcaseOre;
    if (ore !== null) {
      const t = MINERAL_TYPES[ore];
      const q = world.holdManifest().find((h) => h.i === ore);
      drawValue(c, (t?.label ?? '').toUpperCase(), x + u(12), y + vh - u(12), PALE, u(12), 'left', 3);
      let oy = y + vh + u(22);
      const line = (k: string, v: string) => {
        drawLabel(c, k, x + u(4), oy, DIM, u(10));
        drawValue(c, v, x + w - u(4), oy, ICE, u(12), 'right', 1);
        oy += u(19);
      };
      line('CLASS', (t?.kind ?? '').toUpperCase());
      line('ABOARD', `${q?.n ?? 0} SHARDS`);
      line('EACH', `${q?.each ?? 0} SALVAGE`);
      line('STACK', `${(q?.n ?? 0) * (q?.each ?? 0)} SALVAGE`);
      // Wrapped, because it is prose rather than a number and the rail is
      // narrow. This is the one line that says why the stuff is worth mining.
      const words = (t?.usedFor ?? '').toUpperCase().split(' ');
      let ln = '';
      oy += u(6);
      for (const wd of words) {
        const next = ln ? `${ln} ${wd}` : wd;
        c.font = `${u(10)}px ${MONO}`;
        if (c.measureText(next).width > w - u(8) && ln) {
          drawLabel(c, ln, x + u(4), oy, DIM, u(10));
          oy += u(14);
          ln = wd;
        } else ln = next;
      }
      if (ln) drawLabel(c, ln, x + u(4), oy, DIM, u(10));
      return;
    }

    const ch = world.player.chassis;
    drawValue(c, ch.name.toUpperCase(), x + u(12), y + vh - u(12), PALE, u(12), 'left', 3);

    // The specs. Absolute numbers where the player has an intuition for the
    // unit, and multipliers where they do not — "1.4x thrust" says more than a
    // force in newtons ever could.
    const S = world.player.stats;
    let ly = y + vh + u(22);
    for (const spec of SPECS) {
      const val = spec.get(S);
      drawLabel(c, spec.name, x + u(4), ly, DIM, u(10));
      drawValue(c, val, x + w - u(4), ly, ICE, u(12), 'right', 1);
      ly += u(19);
    }
  }

  /**
   * The ore index the turntable should be showing, or null for the boat.
   *
   * Read off the SELECTED ROW rather than tracked as its own state, so it
   * cannot drift out of step with the cursor — the thing being displayed and
   * the thing being sold are the same row by construction.
   */
  get showcaseOre(): number | null {
    if (this.screen !== 'hold') return null;
    const r = this.rows[this.sel];
    return r?.qty ? r.qty.ore : null;
  }

  private panel(x: number, y: number, w: number, h: number, heading: string, u: (v: number) => number): number {
    const c = this.ctx;
    c.fillStyle = 'rgba(4,22,28,0.88)';
    c.fillRect(x, y, w, h);
    drawFrame(c, x, y, w, h, 'rgba(95,228,224,0.22)', 'rgba(95,228,224,0.6)', u(14));
    if (!heading) return x + u(20);
    drawValue(c, heading, x + u(20), y + u(26), CYAN, u(17), 'left', 4);
    c.fillStyle = 'rgba(95,228,224,0.18)';
    c.fillRect(x + u(20), y + u(36), w - u(40), 1);
    // measureText ignores tracking, so add it back a character at a time.
    return x + u(20) + c.measureText(heading).width + heading.length * 4 + u(10);
  }

  /**
   * The shared option list. Rows are big, centred and obvious — this is the
   * control surface a seven year old actually uses.
   */
  private options(rows: Row[], cx: number, top: number, w: number, rowH: number, gap: number, u: (v: number) => number) {
    const c = this.ctx;
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      const x = cx - w / 2;
      const y = top + i * (rowH + gap);
      r.x = x; r.y = y; r.w = w; r.h = rowH;

      const sel = i === this.sel;
      c.globalAlpha = r.enabled ? 1 : 0.34;
      if (sel) {
        c.fillStyle = 'rgba(95,228,224,0.10)';
        c.fillRect(x, y, w, rowH);
        c.fillStyle = AMBER;
        c.fillRect(x, y, u(3), rowH);
      }
      drawFrame(
        c, x, y, w, rowH,
        sel ? 'rgba(95,228,224,0.42)' : 'rgba(95,228,224,0.14)',
        sel ? 'rgba(255,180,84,0.9)' : 'rgba(95,228,224,0.3)',
        u(10),
      );

      const ty = y + rowH * (r.sub ? 0.44 : 0.62);
      drawValue(c, r.label, cx, ty, sel ? WHITE : CYAN, u(18), 'center', 3);
      if (r.sub) drawLabel(c, r.sub, cx, y + rowH * 0.74, DIM, u(9), 'center');
      if (sel) {
        // A caret that breathes, so the cursor is findable at a glance.
        const k = 0.5 + 0.5 * Math.sin(this.time * 4);
        c.globalAlpha = (r.enabled ? 1 : 0.34) * (0.4 + k * 0.6);
        drawValue(c, '>', x + u(14), ty, AMBER, u(16), 'left', 0);
      }
      c.globalAlpha = 1;
    }
  }

  /** Six-cell run summary, shared by pause and the two end cards. */
  private runStats(world: World, x: number, y: number, w: number, u: (v: number) => number, compact: boolean) {
    const c = this.ctx;
    const p = world.player;
    const cells: [string, string][] = [
      ['DEPTH', `${Math.round(p.depth)} m`],
      ['SECTOR', world.region.name],
      ['SCORE', String(p.score).padStart(6, '0')],
      ['SALVAGE', String(world.salvage)],
      ['KILLS', String(world.actors.kills)],
      ['TIME', formatTime(world.playTime)],
    ];
    const cols = compact ? 3 : 3;
    const cw = w / cols;
    const rh = u(compact ? 34 : 42);
    for (let i = 0; i < cells.length; i++) {
      const cx = x + (i % cols) * cw;
      const cy = y + Math.floor(i / cols) * rh;
      drawLabel(c, cells[i][0], cx, cy, DIM, u(9));
      drawValue(c, cells[i][1], cx, cy + u(compact ? 17 : 21), CYAN, u(compact ? 13 : 16), 'left', 1);
    }
  }

  // --- title ---------------------------------------------------------------

  private title(view: MenuView) {
    const c = this.ctx;
    const { w: W, h: H } = view;
    const u = (v: number) => v * view.s;

    // The live game renders behind this card, so the wash is a scrim, not a
    // background: enough to seat the wordmark, sheer enough that the water,
    // the kelp and the drifting sediment still read through it.
    const g = c.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, 'rgba(10,76,86,0.26)');
    g.addColorStop(0.28, 'rgba(6,48,57,0.36)');
    g.addColorStop(0.62, 'rgba(2,22,29,0.52)');
    g.addColorStop(1, 'rgba(1,7,10,0.70)');
    c.fillStyle = g;
    c.fillRect(0, 0, W, H);

    // A soft pool behind the middle of the card so the type never has to fight
    // whatever the scene happens to be doing under it.
    const pool = c.createRadialGradient(
      W * 0.5, H * 0.44, 0,
      W * 0.5, H * 0.44, Math.max(W, H) * 0.52,
    );
    pool.addColorStop(0, 'rgba(1,10,14,0.62)');
    pool.addColorStop(0.55, 'rgba(1,10,14,0.34)');
    pool.addColorStop(1, 'rgba(1,10,14,0)');
    c.fillStyle = pool;
    c.fillRect(0, 0, W, H);

    // Shafts of surface light, leaning as they sink.
    c.save();
    c.globalCompositeOperation = 'lighter';
    for (let i = 0; i < 5; i++) {
      const t = this.time * 0.07 + i * 1.7;
      const x = W * (0.1 + 0.2 * i) + Math.sin(t) * W * 0.05;
      const wd = W * (0.035 + 0.02 * Math.sin(t * 1.3 + i));
      const sg = c.createLinearGradient(x, 0, x + W * 0.1, H * 0.8);
      sg.addColorStop(0, 'rgba(120,240,235,0.055)');
      sg.addColorStop(1, 'rgba(120,240,235,0)');
      c.fillStyle = sg;
      c.beginPath();
      c.moveTo(x - wd, 0);
      c.lineTo(x + wd, 0);
      c.lineTo(x + W * 0.1 + wd * 2.4, H * 0.85);
      c.lineTo(x + W * 0.1 - wd * 2.4, H * 0.85);
      c.closePath();
      c.fill();
    }
    c.restore();

    // Sonar sweep behind the wordmark.
    const scx = W * 0.5;
    const scy = H * 0.52;
    const R = Math.min(W, H) * 0.44;
    c.save();
    c.globalCompositeOperation = 'lighter';
    c.strokeStyle = 'rgba(95,228,224,0.16)';
    c.lineWidth = 1;
    for (let i = 1; i <= 4; i++) {
      c.beginPath();
      c.arc(scx, scy, (R * i) / 4, 0, TAU);
      c.stroke();
    }
    c.globalAlpha = 0.5;
    for (let i = 0; i < 24; i++) {
      const a = (i / 24) * TAU;
      const inner = i % 2 ? 0.975 : 0.95;
      c.beginPath();
      c.moveTo(scx + Math.cos(a) * R * inner, scy + Math.sin(a) * R * inner);
      c.lineTo(scx + Math.cos(a) * R, scy + Math.sin(a) * R);
      c.stroke();
    }
    c.globalAlpha = 1;

    // The sweep itself: a wedge trailing a bright leading edge.
    const sweep = (this.time * 0.55) % TAU;
    const wedge = c.createRadialGradient(scx, scy, 0, scx, scy, R);
    wedge.addColorStop(0, 'rgba(95,228,224,0.72)');
    wedge.addColorStop(0.5, 'rgba(95,228,224,0.34)');
    wedge.addColorStop(1, 'rgba(95,228,224,0.03)');
    c.fillStyle = wedge;
    for (let i = 0; i < 5; i++) {
      // Stacked slices give the trail a falloff a single arc cannot.
      c.globalAlpha = 0.5 - i * 0.09;
      c.beginPath();
      c.moveTo(scx, scy);
      c.arc(scx, scy, R, sweep - 0.16 * (i + 1), sweep - 0.16 * i);
      c.closePath();
      c.fill();
    }
    c.globalAlpha = 1;
    // The leading edge fades out toward the rim rather than ruling a hard line
    // across the scene now that there is a scene to rule it across.
    const edge = c.createLinearGradient(
      scx, scy,
      scx + Math.cos(sweep) * R, scy + Math.sin(sweep) * R,
    );
    edge.addColorStop(0, 'rgba(190,255,250,0.55)');
    edge.addColorStop(0.65, 'rgba(190,255,250,0.22)');
    edge.addColorStop(1, 'rgba(190,255,250,0)');
    c.strokeStyle = edge;
    c.lineWidth = 1.5;
    c.beginPath();
    c.moveTo(scx, scy);
    c.lineTo(scx + Math.cos(sweep) * R, scy + Math.sin(sweep) * R);
    c.stroke();

    // Contacts light up as the beam crosses them and fade behind it.
    for (const b of this.blips) {
      let d = sweep - b.a;
      d = ((d % TAU) + TAU) % TAU;
      const k = Math.max(0, 1 - d / 1.9);
      if (k <= 0.01) continue;
      c.globalAlpha = k * 0.9;
      c.fillStyle = k > 0.75 ? AMBER : CYAN;
      c.beginPath();
      c.arc(scx + Math.cos(b.a) * R * b.d, scy + Math.sin(b.a) * R * b.d, u(3), 0, TAU);
      c.fill();
      c.globalAlpha = 1;
    }
    c.restore();

    // Marine snow, rising.
    c.fillStyle = 'rgba(180,240,238,0.35)';
    for (const m of this.motes) {
      const y = (((m.y - this.time * m.sp) % 1) + 1) % 1;
      const x = (m.x + Math.sin(this.time * 0.25 + m.ph) * 0.012) % 1;
      c.globalAlpha = 0.12 + 0.3 * (1 - y);
      c.beginPath();
      c.arc(x * W, y * H, m.r, 0, TAU);
      c.fill();
    }
    c.globalAlpha = 1;

    // Wordmark.
    const titleY = H * 0.34;
    c.save();
    c.shadowColor = 'rgba(95,228,224,0.75)';
    c.shadowBlur = u(34);
    drawValue(c, 'A B Y S S', W * 0.5, titleY, '#dffdfb', u(view.compact ? 62 : 88), 'center', u(10));
    c.restore();
    drawLabel(c, 'DESCEND UNTIL THE LIGHT GIVES OUT', W * 0.5, titleY + u(30), 'rgba(255,180,84,0.85)', u(11), 'center', 5);

    // Rule under the wordmark.
    c.strokeStyle = 'rgba(95,228,224,0.25)';
    c.lineWidth = 1;
    c.beginPath();
    c.moveTo(W * 0.5 - u(220), titleY + u(46) + 0.5);
    c.lineTo(W * 0.5 + u(220), titleY + u(46) + 0.5);
    c.stroke();

    const rowW = Math.min(u(420), W * 0.72);
    const rowH = u(view.compact ? 44 : 52);
    this.options(this.rows, W * 0.5, H * 0.58, rowW, rowH, u(12), u);

    this.controls(view, u);

    // Frame the whole card so it reads as an instrument, not a poster.
    drawFrame(
      this.ctx,
      view.safe.left + u(12), view.safe.top + u(12),
      W - view.safe.left - view.safe.right - u(24),
      H - view.safe.top - view.safe.bottom - u(24),
      'rgba(95,228,224,0.14)', 'rgba(95,228,224,0.5)', u(22),
    );
  }

  /** Controls summary along the foot of the title card. */
  private controls(view: MenuView, u: (v: number) => number) {
    const c = this.ctx;
    const touchy = view.compact;
    const pairs: [string, string][] = touchy
      ? [['STICK', 'MOVE'], ['FIRE', 'CANNON'], ['TORP', 'TORPEDO'], ['SONAR', 'PING'],
         ['NITRO', 'BURST'], ['DOCK', 'REFIT']]
      : [['WASD', 'MOVE'], ['SPACE', 'CANNON'], ['Q', 'TORPEDO'], ['E', 'SONAR'],
         ['SHIFT', 'NITRO'], ['C', 'DEPTH CHARGE'], ['L', 'LAMP'], ['U', 'DOCK'],
         ['TAB', 'CHART'], ['ESC', 'PAUSE'],
         ['G', 'GRAPPLE'], ['- =', 'ZOOM'], ['PINCH', 'ZOOM'], ['SCROLL', 'PAN'],
         ['0', 'RESET VIEW']];

    const cols = touchy ? 3 : 5;
    const rows = Math.ceil(pairs.length / cols);
    const y = view.h - view.safe.bottom - u(26) - (rows - 1) * u(16);
    const cw = Math.min(u(150), (view.w * 0.8) / cols);
    const x0 = view.w * 0.5 - (cw * cols) / 2;
    drawLabel(c, 'CONTROLS', view.w * 0.5, y - u(18), DIM, u(9), 'center', 4);
    c.globalAlpha = 0.75;
    for (let i = 0; i < pairs.length; i++) {
      const x = x0 + (i % cols) * cw;
      const yy = y + Math.floor(i / cols) * u(16);
      drawValue(c, pairs[i][0], x, yy, CYAN, u(10), 'left', 1);
      drawLabel(c, pairs[i][1], x + u(46), yy, DIM, u(9));
    }
    c.globalAlpha = 1;
  }

  // --- pause ---------------------------------------------------------------

  private paused(world: World, view: MenuView) {
    const c = this.ctx;
    const { w: W, h: H } = view;
    const u = (v: number) => v * view.s;
    this.scrim(view, 0.72);

    const rows = this.rows;
    const rowH = u(view.compact ? 34 : 42);
    const gap = u(8);
    const listH = rows.length * (rowH + gap) - gap;
    const statsH = u(view.compact ? 68 : 84);

    const pw = Math.min(u(660), W * 0.9);
    const ph = Math.min(u(66) + statsH + u(18) + listH + u(22), H - view.safe.top - view.safe.bottom - u(20));
    const px = W * 0.5 - pw / 2;
    const py = H * 0.5 - ph / 2;
    this.panel(px, py, pw, ph, 'DIVE SUSPENDED', u);

    this.runStats(world, px + u(24), py + u(66), pw - u(48), u, view.compact);
    this.options(rows, W * 0.5, py + ph - u(22) - listH, pw - u(60), rowH, gap, u);

    drawLabel(c, 'ESC RESUMES', px + pw - u(20), py + u(26), DIM, u(9), 'right');
  }

  /**
   * Options. Deliberately the same panel as everything else: this is a page in
   * the boat's own instrument set, not a browser settings dialog dropped on
   * top of it. Every row is a cycle rather than a slider — one control that
   * works identically under a thumb, a stick and a key.
   */
  private options_(view: MenuView) {
    this.listPanel(view, 'OPTIONS', 'FIRE / ENTER CYCLES  ·  ESC GOES BACK');
  }

  /**
   * Mode select. Taller rows than the options list, because a mode is a
   * paragraph rather than a toggle and the blurb is the whole reason a player
   * picks one over the other.
   */
  private modes_(view: MenuView) {
    this.listPanel(view, 'CHOOSE A DIVE', 'FIRE / ENTER BEGINS  ·  ESC GOES BACK', 60);
  }

  /** The between-waves pick. */
  private cards_(view: MenuView) {
    this.listPanel(view, 'SALVAGED SYSTEMS', 'THIS RUN ONLY  ·  FIRE / ENTER TAKES IT', 62);
  }

  /** How far the panel list is scrolled, in pixels. Follows the selection. */
  private listScroll = 0;

  /** The shared list-in-a-panel these are. */
  private listPanel(view: MenuView, heading: string, footer: string, rowPx = 46) {
    const c = this.ctx;
    const { w: W, h: H } = view;
    const u = (v: number) => v * view.s;
    this.scrim(view, 0.82);

    const rows = this.rows;
    const rowH = u(view.compact ? rowPx * 0.78 : rowPx);
    const gap = u(8);
    const listH = rows.length * (rowH + gap) - gap;

    const pw = Math.min(u(660), W * 0.9);
    const ph = Math.min(u(66) + listH + u(26), H - view.safe.top - view.safe.bottom - u(20));
    const px = W * 0.5 - pw / 2;
    const py = H * 0.5 - ph / 2;
    this.panel(px, py, pw, ph, heading, u);

    // SCROLLED, because the list outgrew the screen.
    //
    // Options has been added to steadily — a graphics section, a time of day,
    // now a tuner — and the panel was already clamped to the display while the
    // rows carried on being laid out from the top at a fixed pitch. Everything
    // past the bottom edge was drawn off-screen: still selectable by keyboard,
    // still invisible, which is the worst of both.
    //
    // The list follows the SELECTION rather than offering a scrollbar. A menu
    // driven by a stick and a keyboard has no pointer to drag with, so the only
    // thing that can move the view is the cursor, and keeping it a row and a
    // half from either edge means the next item is always already visible.
    const listTop = py + u(58);
    const listView = ph - u(58) - u(26);
    const over = Math.max(0, listH - listView);
    if (over > 0) {
      const pitch = rowH + gap;
      const selY = this.sel * pitch;
      const margin = pitch * 1.5;
      this.listScroll = Math.min(
        Math.max(this.listScroll, selY + rowH + margin - listView),
        Math.max(0, selY - margin),
      );
    }
    this.listScroll = Math.min(Math.max(this.listScroll, 0), over);

    c.save();
    c.beginPath();
    c.rect(px, listTop, pw, listView);
    c.clip();
    this.options(rows, W * 0.5, listTop - this.listScroll, pw - u(48), rowH, gap, u);
    c.restore();

    // Say so when there is more. A clipped list with no edge marker reads as a
    // list that simply ends.
    if (this.listScroll > 1) {
      drawLabel(c, '\u25b2', W * 0.5, listTop + u(9), DIM, u(9), 'center');
    }
    if (this.listScroll < over - 1) {
      drawLabel(c, '\u25bc', W * 0.5, listTop + listView - u(3), DIM, u(9), 'center');
    }

    drawLabel(c, footer, W * 0.5, py + ph - u(12), DIM, u(9), 'center');
  }

  // --- shop ----------------------------------------------------------------

  private shop(world: World, view: MenuView) {
    const c = this.ctx;
    const { w: W, h: H } = view;
    const u = (v: number) => v * view.s;
    this.scrim(view, 0.82);

    // The panel is sized to its rows, not to the screen: a half-empty box reads
    // as something broken rather than as something finished.
    const rows = this.rows;
    const gap = u(6);
    const avail = H - view.safe.top - view.safe.bottom - u(112);
    const rowH = clamp(avail / rows.length - gap, u(26), u(56));
    const listH = rows.length * (rowH + gap) - gap;

    // WIDER, AND SPLIT IN TWO.
    //
    // The panel was 860 units of rows and nothing else: a price list. Docking
    // is the one moment the player stops and thinks about the boat, and the
    // boat itself was the one thing not on screen — every number describing it
    // lived behind the row you were hovering, one at a time.
    //
    // So the rows keep the left, and a rail on the right carries the machine:
    // the hull turning over at the top, and what it currently DOES underneath.
    // Below `railMin` there is no room for two columns and it collapses back to
    // the list, which is the phone case and the small-window case.
    // 400, not 300. The turntable is the point of the rail and at 300 the
    // boat was a thumbnail; the spec column was reading narrow with it.
    const rail = W * 0.94 >= u(1040) ? u(400) : 0;
    const pw = Math.min(u(860) + (rail ? rail + u(20) : 0), W * 0.94);
    const px = W * 0.5 - pw / 2;
    const listW = pw - u(40) - (rail ? rail + u(20) : 0);
    // The rail wants room for the viewport and the specs under it; if the rows
    // alone are shorter than that, the panel grows to fit the rail instead.
    const railH = rail ? u(238) + SPECS.length * u(19) + u(30) : 0;
    const ph = u(56) + Math.max(listH, railH) + u(26);
    const py = Math.max(view.safe.top + u(12), H * 0.5 - ph / 2);
    const afterHeading = this.panel(
      px, py, pw, ph,
      this.screen === 'hold' ? 'HOLD'
        : this.screen === 'bay'
          ? (UPGRADE_BAYS.find((b) => b.id === this.bay)?.name ?? 'REFIT')
          : 'REFIT', u);

    drawLabel(c, world.dockable ? world.dockable.name : 'FIELD REFIT', afterHeading, py + u(26), DIM, u(10));

    // The balance, big: knowing what you can afford is the whole screen.
    const balX = px + pw - u(20);
    drawLabel(c, 'SALVAGE', balX, py + u(16), DIM, u(9), 'right');
    drawValue(c, String(world.salvage), balX, py + u(40), AMBER, u(26), 'right', 1);
    drawDiamond(c, balX - c.measureText(String(world.salvage)).width - u(20), py + u(32), u(7), AMBER);

    if (this.flash !== 0) {
      // Green wash on a purchase, red on a refusal — instant, wordless feedback.
      c.globalAlpha = Math.min(0.5, Math.abs(this.flash) * 0.35);
      c.fillStyle = this.flash > 0 ? '#5fffa8' : RED;
      c.fillRect(px, py, pw, ph);
      c.globalAlpha = 1;
    }

    const top = py + u(50);
    const iw = listW;

    if (rail) this.specRail(world, view, px + u(20) + listW + u(20), top, rail, u);

    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      const x = px + u(20);
      const y = top + i * (rowH + gap);
      r.x = x; r.y = y; r.w = iw; r.h = rowH;
      const sel = i === this.sel;

      if (r.id === 'leave') {
        if (sel) {
          c.fillStyle = 'rgba(95,228,224,0.10)';
          c.fillRect(x, y, iw, rowH);
        }
        drawFrame(c, x, y, iw, rowH,
          sel ? 'rgba(95,228,224,0.42)' : 'rgba(95,228,224,0.14)',
          sel ? 'rgba(255,180,84,0.9)' : 'rgba(95,228,224,0.3)', u(10));
        drawValue(c, 'LEAVE REFIT', x + iw / 2, y + rowH * 0.62, sel ? WHITE : CYAN, u(16), 'center', 3);
        continue;
      }

      // Cargo. It is the one row that pays *out*, so it is the one row drawn
      // in the reward colour rather than the price colour — and it must not
      // fall through to the module path below, where a null cost means MAXED.
      if (r.sells) {
        if (sel) {
          c.fillStyle = 'rgba(143,217,255,0.12)';
          c.fillRect(x, y, iw, rowH);
          c.fillStyle = ICE;
          c.fillRect(x, y, u(3), rowH);
        }
        drawFrame(c, x, y, iw, rowH,
          sel ? 'rgba(143,217,255,0.45)' : 'rgba(143,217,255,0.16)',
          sel ? 'rgba(143,217,255,0.95)' : 'rgba(143,217,255,0.34)', u(10));
        drawValue(c, r.label, x + u(16), y + rowH * 0.44, sel ? WHITE : ICE, u(view.compact ? 13 : 16), 'left', 2);
        drawLabel(c, r.sub, x + u(16), y + rowH * 0.78, 'rgba(143,217,255,0.85)', u(view.compact ? 9 : 11));
        const payX = x + iw - u(16);
        // AN ORE ROW SHOWS WHAT IT WOULD SELL AND FOR HOW MUCH, and the arrows
        // are the whole instruction: a quantity with nothing indicating it can
        // be changed is a quantity nobody tries to change. Only on the selected
        // row, because only the selected row takes the keys.
        // AN ORE ROW SHOWS WHAT IT WOULD SELL AND FOR HOW MUCH. The arrows are
        // the whole instruction: a quantity with nothing to say it can be
        // changed is a quantity nobody tries to change. Only on the selected
        // row, because only the selected row takes the keys.
        const paid = r.qty ? `+${r.qty.value * r.qty.each}` : `+${world.cargoValue}`;
        drawValue(c, paid, payX, y + rowH * 0.62, ICE, u(view.compact ? 15 : 19), 'right', 1);
        c.font = `600 ${u(view.compact ? 15 : 19)}px ${MONO}`;
        const paidW = c.measureText(paid).width;
        if (r.qty) {
          drawValue(c, sel ? `< ${r.qty.value} >` : `${r.qty.value}`,
                    payX - paidW - u(20), y + rowH * 0.62,
                    sel ? WHITE : 'rgba(143,217,255,0.7)', u(view.compact ? 13 : 16), 'right', 1);
        }
        drawDiamond(c, payX - paidW - u(12), y + rowH * 0.5, u(6), ICE);
        continue;
      }

      // A hull. Drawn in the reward colour rather than the price colour when
      // it is already yours, and it must not fall through to the module path
      // below, where a null cost means MAXED.
      if (r.hull) {
        const owned = world.hulls.has(r.hull);
        const active = world.player.chassis.id === r.hull;
        const col = active ? PALE : owned ? ICE : r.afford ? AMBER : RED;
        if (sel) {
          c.fillStyle = 'rgba(201,140,255,0.10)';
          c.fillRect(x, y, iw, rowH);
          c.fillStyle = col;
          c.fillRect(x, y, u(3), rowH);
        }
        drawFrame(c, x, y, iw, rowH,
          sel ? 'rgba(201,140,255,0.42)' : 'rgba(201,140,255,0.14)',
          sel ? 'rgba(201,140,255,0.9)' : 'rgba(201,140,255,0.30)', u(10));
        c.globalAlpha = active ? 0.75 : r.afford ? 1 : 0.42;
        drawValue(c, r.label, x + u(16), y + rowH * 0.44, sel ? WHITE : col, u(view.compact ? 13 : 16), 'left', 2);
        drawLabel(c, r.sub, x + u(16), y + rowH * 0.78, 'rgba(160,220,220,0.8)', u(view.compact ? 9 : 11));
        const px2 = x + iw - u(16);
        if (active) {
          drawValue(c, 'ACTIVE', px2, y + rowH * 0.62, PALE, u(13), 'right', 2);
        } else if (owned) {
          drawValue(c, 'OWNED', px2, y + rowH * 0.62, ICE, u(13), 'right', 2);
        } else {
          const cost = r.cost ?? 0;
          const pc = r.afford ? AMBER : RED;
          drawValue(c, String(cost), px2, y + rowH * 0.62, pc, u(view.compact ? 15 : 19), 'right', 1);
          c.font = `600 ${u(view.compact ? 15 : 19)}px ${MONO}`;
          drawDiamond(c, px2 - c.measureText(String(cost)).width - u(12), y + rowH * 0.5, u(6), pc);
        }
        c.globalAlpha = 1;
        continue;
      }

      const lvl = r.upgrade ? world.levels[r.upgrade] : 0;
      const max = r.upgrade ? maxLevel(r.upgrade) : 0;
      const maxed = r.cost === null;
      const afford = r.afford;

      if (sel) {
        c.fillStyle = 'rgba(95,228,224,0.10)';
        c.fillRect(x, y, iw, rowH);
        c.fillStyle = afford ? AMBER : DIM;
        c.fillRect(x, y, u(3), rowH);
      }
      drawFrame(c, x, y, iw, rowH,
        sel ? 'rgba(95,228,224,0.40)' : 'rgba(95,228,224,0.12)',
        sel ? 'rgba(255,180,84,0.85)' : 'rgba(95,228,224,0.26)', u(10));

      // Anything you cannot buy right now is visibly out of reach.
      c.globalAlpha = maxed ? 0.6 : afford ? 1 : 0.42;

      drawValue(c, r.label, x + u(16), y + rowH * 0.44, sel ? WHITE : CYAN, u(view.compact ? 13 : 16), 'left', 2);
      this.pips(x + u(16), y + rowH * 0.74, lvl, max, u);
      drawLabel(
        c, maxed ? 'FULLY FITTED' : r.sub, x + iw * 0.42, y + rowH * 0.58,
        maxed ? DIM : 'rgba(160,220,220,0.85)', u(view.compact ? 9 : 11),
      );

      const priceX = x + iw - u(16);
      if (maxed) {
        drawValue(c, 'MAXED', priceX, y + rowH * 0.62, DIM, u(14), 'right', 2);
      } else {
        const cost = r.cost ?? 0;
        const col = afford ? AMBER : RED;
        drawValue(c, String(cost), priceX, y + rowH * 0.62, col, u(view.compact ? 15 : 19), 'right', 1);
        c.font = `600 ${u(view.compact ? 15 : 19)}px ${MONO}`;
        drawDiamond(c, priceX - c.measureText(String(cost)).width - u(12), y + rowH * 0.5, u(6), col);
      }
      c.globalAlpha = 1;
    }

    drawLabel(
      c,
      view.compact ? 'TAP A MODULE TO FIT IT' : 'FIRE / ENTER FITS THE MODULE   ·   ESC LEAVES',
      px + pw / 2, py + ph - u(9), DIM, u(9), 'center',
    );
  }

  /** Level readout: filled squares for what is fitted, hollow for what is not. */
  private pips(x: number, y: number, level: number, max: number, u: (v: number) => number) {
    const c = this.ctx;
    const s = u(8);
    const gap = u(4);
    for (let i = 0; i < max; i++) {
      const px = x + i * (s + gap);
      if (i < level) {
        c.fillStyle = AMBER;
        c.fillRect(px, y - s, s, s);
      } else {
        c.strokeStyle = 'rgba(95,228,224,0.35)';
        c.lineWidth = 1;
        c.strokeRect(px + 0.5, y - s + 0.5, s - 1, s - 1);
      }
    }
  }

  // --- chart ---------------------------------------------------------------

  private map(world: World, view: MenuView) {
    const c = this.ctx;
    const { w: W, h: H } = view;
    const u = (v: number) => v * view.s;
    this.scrim(view, 0.86);

    const pw = Math.min(u(1040), W * 0.94);
    const px = W * 0.5 - pw / 2;
    const ph = Math.min(u(520), H * 0.78);
    const py = H * 0.5 - ph / 2 - u(10);
    this.panel(px, py, pw, ph, 'DIVE CHART', u);

    const gx = px + u(52);
    const gy = py + u(52);
    const gw = pw - u(72);
    const gh = ph - u(84);

    // Seabed silhouette, straight off the authored profile.
    const n = this.mapY.length;
    let lo = Infinity, hi = -Infinity;
    for (let i = 0; i < n; i++) {
      const v = profileY(WORLD.startX + (i / (n - 1)) * (WORLD.endX - WORLD.startX));
      this.mapY[i] = v;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    // A little headroom above the shallowest point, so the surface is visible.
    hi = Math.max(hi, 0);
    const span = Math.max(1, hi - lo);
    const toX = (wx: number) =>
      gx + clamp((wx - WORLD.startX) / (WORLD.endX - WORLD.startX), 0, 1) * gw;
    const toY = (wy: number) => gy + clamp((hi - wy) / span, 0, 1) * gh;

    c.save();
    c.beginPath();
    c.rect(gx, gy, gw, gh);
    c.clip();

    c.fillStyle = 'rgba(3,18,24,0.7)';
    c.fillRect(gx, gy, gw, gh);

    // Depth gridlines. Their labels are drawn outside the clip, below.
    c.strokeStyle = 'rgba(95,228,224,0.08)';
    c.lineWidth = 1;
    for (let d = 0; d <= 2500; d += 500) {
      const yy = toY(-d);
      c.beginPath();
      c.moveTo(gx, yy + 0.5);
      c.lineTo(gx + gw, yy + 0.5);
      c.stroke();
    }

    c.beginPath();
    c.moveTo(gx, gy + gh);
    for (let i = 0; i < n; i++) {
      c.lineTo(toX(WORLD.startX + (i / (n - 1)) * (WORLD.endX - WORLD.startX)), toY(this.mapY[i]));
    }
    c.lineTo(gx + gw, gy + gh);
    c.closePath();
    c.fillStyle = 'rgba(46,168,164,0.20)';
    c.fill();
    c.strokeStyle = 'rgba(95,228,224,0.7)';
    c.lineWidth = 1.4;
    c.stroke();

    // Everything past the furthest point reached is off the chart.
    const rx = toX(world.reachedX);
    if (rx < gx + gw) {
      c.fillStyle = 'rgba(2,10,14,0.72)';
      c.fillRect(rx, gy, gx + gw - rx, gh);
      c.save();
      c.beginPath();
      c.rect(rx, gy, gx + gw - rx, gh);
      c.clip();
      c.strokeStyle = 'rgba(95,228,224,0.07)';
      for (let hx = rx; hx < gx + gw + gh; hx += u(11)) {
        c.beginPath();
        c.moveTo(hx, gy);
        c.lineTo(hx - gh, gy + gh);
        c.stroke();
      }
      c.restore();
      c.strokeStyle = 'rgba(95,228,224,0.35)';
      c.setLineDash([u(4), u(4)]);
      c.beginPath();
      c.moveTo(rx + 0.5, gy);
      c.lineTo(rx + 0.5, gy + gh);
      c.stroke();
      c.setLineDash([]);
      drawLabel(c, 'UNCHARTED', Math.min(rx + u(10), gx + gw - u(6)), gy + u(14), 'rgba(95,228,224,0.45)', u(9));
    }
    c.restore();

    // Depth scale, in the gutter the graph is clipped away from.
    for (let d = 0; d <= 2500; d += 500) {
      drawLabel(c, d === 0 ? '0 m' : String(d), gx - u(8), toY(-d) + u(3), DIM, u(8), 'right');
    }

    // Stations. Activated ones are lit and named; the rest are hollow ghosts.
    for (let i = 0; i < STATIONS.length; i++) {
      const s = STATIONS[i];
      const sx = toX(s.x);
      const sy = toY(s.y);
      const on = world.activatedStations.has(s.id);
      c.strokeStyle = on ? AMBER : 'rgba(95,228,224,0.30)';
      c.lineWidth = 1;
      c.beginPath();
      c.moveTo(sx + 0.5, sy);
      c.lineTo(sx + 0.5, gy);
      c.stroke();
      if (on) {
        c.fillStyle = AMBER;
        c.beginPath();
        c.arc(sx, sy, u(4), 0, TAU);
        c.fill();
      } else {
        c.strokeStyle = 'rgba(95,228,224,0.5)';
        c.beginPath();
        c.arc(sx, sy, u(3.5), 0, TAU);
        c.stroke();
      }
      if (!view.compact) {
        // Stagger the names so thirteen labels fit across the chart.
        drawLabel(
          c, s.name, sx, gy - u(i % 2 ? 16 : 5),
          on ? 'rgba(255,180,84,0.85)' : 'rgba(78,150,158,0.9)', u(7.5), 'center', 0.5,
        );
      }
    }

    // The sub.
    const sx = toX(world.player.x);
    const sy = toY(world.player.y);
    c.strokeStyle = 'rgba(95,228,224,0.5)';
    c.setLineDash([u(3), u(3)]);
    c.beginPath();
    c.moveTo(sx + 0.5, gy);
    c.lineTo(sx + 0.5, gy + gh);
    c.stroke();
    c.setLineDash([]);
    drawSubGlyph(c, sx, sy, u(7), WHITE);
    drawLabel(c, `${Math.round(world.player.depth)} m`, sx, sy + u(20), WHITE, u(9), 'center');

    drawLabel(
      c,
      `${Math.round(world.progress * 100)}% TRAVERSED   ·   ${world.activatedStations.size}/${STATIONS.length} STATIONS`,
      px + u(20), py + ph - u(14), DIM, u(10),
    );

    const rowW = Math.min(u(280), pw * 0.5);
    this.options(this.rows, W * 0.5, py + ph + u(14), rowW, u(34), u(8), u);
  }

  // --- end cards -----------------------------------------------------------

  private ending(world: World, view: MenuView, won: boolean) {
    const c = this.ctx;
    const { w: W, h: H } = view;
    const u = (v: number) => v * view.s;
    this.scrim(view, won ? 0.78 : 0.86);

    if (won) {
      // A little light from below, for the one screen that is good news.
      const g = c.createRadialGradient(W * 0.5, H * 0.62, 0, W * 0.5, H * 0.62, Math.max(W, H) * 0.6);
      g.addColorStop(0, 'rgba(95,228,224,0.16)');
      g.addColorStop(1, 'rgba(95,228,224,0)');
      c.fillStyle = g;
      c.fillRect(0, 0, W, H);
      for (const m of this.motes) {
        const y = (((m.y - this.time * m.sp * 2.2) % 1) + 1) % 1;
        c.globalAlpha = 0.15 + 0.35 * (1 - y);
        c.fillStyle = 'rgba(255,214,150,0.7)';
        c.beginPath();
        c.arc(m.x * W, y * H, m.r, 0, TAU);
        c.fill();
      }
      c.globalAlpha = 1;
    }

    const pw = Math.min(u(640), W * 0.9);
    const ph = Math.min(u(400), H * 0.86);
    const px = W * 0.5 - pw / 2;
    const py = H * 0.5 - ph / 2;
    this.panel(px, py, pw, ph, '', u);

    c.save();
    c.shadowColor = won ? 'rgba(255,180,84,0.7)' : 'rgba(255,90,74,0.6)';
    c.shadowBlur = u(26);
    // A survival run ends on its own terms: it has a wave and a score rather
    // than a depth and a salvage total, and it pays out.
    const surv = !world.rules.persists && world.rules.wave !== undefined;
    const r = surv ? survivalRun() : null;
    drawValue(c,
      won ? 'THE HEART' : surv ? 'OVERRUN' : 'HULL LOST',
      W * 0.5, py + u(62), won ? AMBER : RED, u(38), 'center', 6);
    c.restore();
    drawLabel(
      c,
      won ? 'YOU REACHED THE BOTTOM OF THE WORLD'
        : surv ? `THE SHELF HELD FOR ${r!.wave} WAVE${r!.wave === 1 ? '' : 'S'}`
        : 'THE SALVAGE TEAM WILL COME FOR YOU',
      W * 0.5, py + u(84), DIM, u(10), 'center',
    );

    if (surv && r) {
      const rec = loadSurvivalRecord();
      const col = (i: number) => px + u(28) + i * ((pw - u(56)) / 3);
      const cell = (i: number, k: string, v: string, hl = CYAN) => {
        drawLabel(c, k, col(i), py + u(126), DIM, u(9));
        drawValue(c, v, col(i), py + u(150), hl, u(20), 'left', 1);
      };
      cell(0, 'WAVE', String(r.wave), r.wave >= rec.bestWave ? AMBER : CYAN);
      cell(1, 'SCORE', String(r.score), r.score >= rec.bestScore ? AMBER : CYAN);
      cell(2, 'CREDITS EARNED', String(Math.round(r.score * 0.02)), ICE);
      drawLabel(c, `BEST  WAVE ${rec.bestWave}  ·  SCORE ${rec.bestScore}  ·  RUNS ${rec.runs}`,
        px + u(28), py + u(180), DIM, u(9));
      drawLabel(c, 'SALVAGED SYSTEMS ARE GONE. THE CREDITS ARE YOURS.',
        px + u(28), py + u(202), 'rgba(160,220,220,0.7)', u(9));
    } else {
      this.runStats(world, px + u(28), py + u(122), pw - u(56), u, view.compact);
    }

    if (won && !surv) {
      let fitted = 0, total = 0;
      for (const def of UPGRADES) {
        fitted += world.levels[def.id];
        total += maxLevel(def.id);
      }
      drawLabel(c, 'MODULES FITTED', px + u(28), py + u(214), DIM, u(9));
      drawValue(c, `${fitted} / ${total}`, px + u(28), py + u(234), CYAN, u(16), 'left', 1);
    }

    const rows = this.rows;
    const rowH = u(view.compact ? 36 : 44);
    const gap = u(8);
    const listH = rows.length * (rowH + gap) - gap;
    this.options(rows, W * 0.5, py + ph - u(24) - listH, pw - u(60), rowH, gap, u);
  }
}
